import Axios from 'axios'
// import { store } from './redux/store';
import { FORBIDDEN_ACTION, TWO_FACTOR_CODE_EXPIRED, TWO_FACTOR_CODE_REQUIRED } from './errorCodes';

let store

export const injectStore = _store => {
    store = _store
}

/**
 * True when the current path is inside the poll flow (`…/poll/…`). Inside the
 * poll flow, 2FA redirects are handled by the poll's own `/verify` page rather
 * than the global dashboard `/verify-2fa` page.
 */
export const isPollFlowPath = (pathname) =>
    typeof pathname === 'string' && pathname.includes('/poll/');

const axios = Axios.create({
    baseURL: process.env.NEXT_PUBLIC_BACKEND_URL,
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
    },
    withCredentials: true,
    withXSRFToken: true
});

axios.interceptors.request.use(
    function (config)
    {
        // Do something before the request is sent
        // For example, add an authentication token to the headers
        const state = store.getState();
        let token = state.app?.token || null;
        const persistState = state._persist;
        const isRehydrated = persistState?.rehydrated;
        const canUseLocalStorage = typeof window !== 'undefined' && typeof localStorage !== 'undefined';

        // Fallback: if redux-persist is in use but hasn't rehydrated yet, read token directly
        // from localStorage to avoid a race-condition 401 on page load.
        if (!token && persistState && !isRehydrated && canUseLocalStorage) {
            try {
                const raw = localStorage.getItem('persist:nextjs');
                if (raw) {
                    const parsed = JSON.parse(raw);
                    if (parsed.app) {
                        const appState = JSON.parse(parsed.app);
                        token = appState.token || null;
                    }
                }
            } catch (_) {
                // Ignore parse errors — fall through to null token
            }
        }

        const roleId = state.app?.roleActingAs?.id || null;
        if (token)
        {
            config.headers.Authorization = `Bearer ${token}`;
        }
        if (roleId)
        {
            config.headers['X-Role-Acting-As'] = roleId;
        }
        return config;
    },
    function (error)
    {
        // Handle the error
        return Promise.reject(error);
    }
);

axios.interceptors.response.use(
    function (response)
    {
        // Do something with the response data
        return response;
    },
    function (error)
    {
        const status = error.response ? error.response.status : null;
        if (!status)
        {
            return Promise.reject(error);
        }
        // Handle the error
        if (error.response.status === 401)
        {
            // Handle unauthorized access
            const errorCode = error.response.data.error_code;
            const redirectUrl = error?.response?.data?.redirect_url || '/verify-2fa';
            switch (errorCode)
            {
                case TWO_FACTOR_CODE_REQUIRED:
                case TWO_FACTOR_CODE_EXPIRED: {
                    // Reject the promise instead of `return window.location.href = ...`
                    // which RESOLVED the promise with the redirect URL string. That made
                    // `await axios.get('/api/user')` in the poll flow return `undefined`,
                    // bouncing demographics <-> poll index in an infinite loop.
                    const inPollFlow =
                        typeof window !== 'undefined' &&
                        isPollFlowPath(window.location.pathname);

                    // Outside the poll flow keep the global fallback redirect to the
                    // dashboard 2FA page; the poll flow routes to its own /verify page.
                    if (!inPollFlow && typeof window !== 'undefined') {
                        window.location.href = redirectUrl;
                    }
                    return Promise.reject(error);
                }
                default: {
                    // Handle other unauthorized access errors.
                    // Be deliberate about when we actually log the user out
                    // to avoid a single transient 401 destroying the session.
                    const appState = store.getState().app || {};

                    // 1. If persist hasn't rehydrated yet, the 401 may be a
                    //    false positive — skip logout.
                    if (!store.getState()._persist?.rehydrated) {
                        return Promise.reject(error);
                    }

                    // 2. If the user wasn't marked as authenticated in the
                    //    first place, this 401 is expected (e.g. a public
                    //    page making an optional auth check). Don't redirect.
                    if (!appState.isAuthenticated) {
                        return Promise.reject(error);
                    }

                    // 3. If the token has already been cleared (cascading
                    //    401s from parallel in-flight requests), skip the
                    //    duplicate logout + redirect.
                    if (!appState.token) {
                        return Promise.reject(error);
                    }

                    store.dispatch({ type: 'app/logOut' });
                    window.location.href = '/login';
                }

            }
        }
        if (error.response.status === 403)
        {
            // Handle forbidden access
            const errorCode = error.response.data.error_code;
            switch (errorCode)
            {
                case FORBIDDEN_ACTION:
                    return window.location.href = '/errors/unauthorized';

            }
        }
        return Promise.reject(error);
    }
);

export default axios